/**
 * Small helpers for parsing the raw Trackwizz payload the backend stores on
 * the case's company. The stored `entityHitResponse` is a Python-repr string
 * ("{'suggestedAction': None, 'hitResponse': [{...}]}"), not real JSON, so
 * we normalise it before parsing.
 *
 * Same logic the credit-appraisal Trackwizz component uses, extracted so the
 * Credit Checker can render the hit summary in-line inside the applicant KYC
 * row and the company screening card without pulling the whole component.
 */

// Turn Python-repr string into a JS-JSON parseable string, then JSON.parse.
export function parseTrackwizzResponse(raw) {
    if (!raw || typeof raw !== "string") return null;
    try {
        const normalised = raw
            .replace(/None/g, "null")
            .replace(/True/g, "true")
            .replace(/False/g, "false")
            .replace(/'/g, '"')
            .trim();
        return JSON.parse(normalised);
    } catch (err) {
        return null;
    }
}

// Collapse [{ source, score, matchType }, ...] into a single string like
// `"UN Sanctions (72%, 65%), OFAC (55%)"` — same shape the appraisal tab uses.
// The matchType label the backend ships is inconsistent across environments
// ("Probable Hit" vs "Probable Match", "Confirm Hit" vs "Confirmed Hit"), so
// callers pass an array of accepted labels and we match any of them.
export function formatHitSources(hitResponse, matchTypes) {
    if (!Array.isArray(hitResponse)) return "";
    const wanted = Array.isArray(matchTypes) ? matchTypes : [matchTypes];
    const wantedLower = wanted.map(t => String(t).toLowerCase());
    const grouped = {};
    hitResponse
        .filter(
            h =>
                h &&
                h.matchType &&
                wantedLower.includes(String(h.matchType).toLowerCase())
        )
        .forEach(h => {
            const src = h.source || "—";
            if (!grouped[src]) grouped[src] = [];
            const scoreStr = h.score !== undefined ? `${h.score}%` : "";
            if (scoreStr && !grouped[src].includes(scoreStr)) {
                grouped[src].push(scoreStr);
            }
        });
    const parts = Object.keys(grouped).map(src => {
        const scores = grouped[src];
        return scores.length ? `${src} (${scores.join(", ")})` : src;
    });
    return parts.join(", ");
}

// Normalise a name for case-insensitive lookup: uppercase, collapse whitespace,
// strip punctuation the backend sometimes leaves in ("Mr.", ",", extra spaces).
function normaliseName(name) {
    if (!name) return "";
    return String(name)
        .toUpperCase()
        .replace(/[.,]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

// Parse a single applicant hit entry. `applicantHitResponses` is a JSONField
// on the model so it usually arrives as real JSON — but some older rows carry
// a Python-repr string per entry, so we defensively call parseTrackwizzResponse
// on strings too.
function extractHitResponse(entry) {
    if (!entry) return null;
    if (typeof entry === "string") {
        const parsed = parseTrackwizzResponse(entry);
        return parsed && parsed.hitResponse ? parsed.hitResponse : null;
    }
    if (entry.hitResponse !== undefined) {
        if (typeof entry.hitResponse === "string") {
            // Nested repr — parse it and dig one level.
            const parsed = parseTrackwizzResponse(entry.hitResponse);
            if (Array.isArray(parsed)) return parsed;
            if (parsed && parsed.hitResponse) return parsed.hitResponse;
            return null;
        }
        return entry.hitResponse;
    }
    return null;
}

/**
 * Build a map keyed by NORMALISED applicant name → { probable, confirmed,
 * reportUrl, index }. Also exposes `_byIndex` and `_length` so callers can
 * fall back to positional lookup when the applicant.name from the frontend
 * doesn't line up character-for-character with the trackwizz payload
 * (case / whitespace / punctuation drift).
 */
const PROBABLE_LABELS = ["Probable Hit", "Probable Match", "probable_hit"];
const CONFIRMED_LABELS = [
    "Confirm Hit",
    "Confirmed Hit",
    "Confirmed Match",
    "Confirm Match",
    "confirm_hit",
    "confirmed_hit"
];

export function parseApplicantHits(trackwizzData) {
    if (!trackwizzData) return null;
    const entity = parseTrackwizzResponse(trackwizzData.entityHitResponse);
    const names = (entity && entity.associates_names) || [];
    const applicantHits = trackwizzData.applicantHitResponses || [];
    const perPdf = String(trackwizzData.applicantReportDataPdf || "").split(",");
    const hasAny =
        (Array.isArray(applicantHits) && applicantHits.length > 0) ||
        (Array.isArray(names) && names.length > 0);
    // We still return a map (never null) when trackwizz data itself exists,
    // so the KYC row can default to "No Hit" (✓) for every applicant instead
    // of a dash. Callers distinguish "no screening record on the case" (null)
    // from "screening ran, no matches for this applicant" (map with default
    // empty strings) via the `_screeningRan` flag below.
    const map = {};
    const byIndex = [];
    const usableLen = Math.max(
        Array.isArray(applicantHits) ? applicantHits.length : 0,
        Array.isArray(names) ? names.length : 0
    );
    for (let i = 0; i < usableLen; i += 1) {
        const entry = Array.isArray(applicantHits) ? applicantHits[i] : null;
        const hitResp = extractHitResponse(entry);
        const record = {
            probable: formatHitSources(hitResp, PROBABLE_LABELS),
            confirmed: formatHitSources(hitResp, CONFIRMED_LABELS),
            reportUrl: (perPdf[i] || "").trim() || null,
            index: i
        };
        byIndex.push(record);
        const name = names[i];
        if (name) {
            map[normaliseName(name)] = record;
        }
    }
    // Expose fallbacks + a flag telling consumers that screening actually
    // ran for this company (even if no hits or no matches for a specific
    // applicant). Non-enumerable so `Object.keys(map)` still returns real
    // applicant name keys.
    Object.defineProperty(map, "_byIndex", { value: byIndex, enumerable: false });
    Object.defineProperty(map, "_length", {
        value: byIndex.length,
        enumerable: false
    });
    Object.defineProperty(map, "_screeningRan", {
        value: hasAny,
        enumerable: false
    });
    return hasAny ? map : null;
}

/**
 * Safely look up an applicant's screening record from the map returned by
 * `parseApplicantHits`. Tries normalised-name first, then positional index.
 * Returns null when there's nothing on file.
 *
 * @param {Object} applicantHits  the map from parseApplicantHits
 * @param {String} name           the frontend-built applicant name
 * @param {Number} index          zero-based position in the applicant list
 */
export function lookupApplicantHit(applicantHits, name, index) {
    if (!applicantHits) return null;
    // Positional first: the OLD Trackwizz component paired applicant records
    // to entity `associates_names` purely by array index. Name matching is a
    // safety net for cases where the frontend's applicant order diverges from
    // trackwizz storage order.
    const byIdx = applicantHits._byIndex;
    if (Array.isArray(byIdx) && index >= 0 && index < byIdx.length) {
        return byIdx[index];
    }
    const byName = applicantHits[normaliseName(name)];
    if (byName) return byName;
    // Screening ran for this company but we can't line this applicant up with
    // a specific record — default to an empty "no hits" record so the KYC row
    // shows the green ✓ instead of a dash.
    if (applicantHits._screeningRan) {
        return { probable: "", confirmed: "", reportUrl: null, index: -1 };
    }
    return null;
}

/**
 * Company (entity-level) screening summary — for the Company Detail card.
 * Returns { action, probable, confirmed, reportUrl, sourceSystemCustomerCode }.
 */
export function parseCompanyScreening(trackwizzData) {
    if (!trackwizzData) return null;
    const entity = parseTrackwizzResponse(trackwizzData.entityHitResponse);
    if (!entity) return null;
    const action = entity.suggestedAction
        ? String(entity.suggestedAction).toUpperCase()
        : null;
    return {
        action,
        probable: formatHitSources(entity.hitResponse, PROBABLE_LABELS),
        confirmed: formatHitSources(entity.hitResponse, CONFIRMED_LABELS),
        reportUrl: trackwizzData.entityPdf || null,
        sourceSystemCustomerCode: entity.sourceSystemCustomerCode || null
    };
}
